import { formApi, formBundleTestingToolApi, formsApi, formsTestingToolApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { FeatureFlag } from 'constants/featureFlags';
import { FormTemplateTypeNewApi } from 'constants/forms';
import { catchAsyncActionError } from 'utils/action';
import { getFormattedDataFormsNewApi, getFormattedFormInfo } from 'utils/forms';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import { selectFormsInfoVersion, selectIsFormsInfoLoaded } from '../selectors/forms.selectors';

const formsSchema = new schema.Entity('data', {}, { idAttribute: 'formId' });

export const FORMS_ACTION_TYPE = createNamedActions('forms', {
  GET_FORMS: 'GET_FORMS',
  GET_FORMS_INFO: 'GET_FORMS_INFO',
  GET_LOAN_SPECIFIC_FORMS: 'GET_LOAN_SPECIFIC_FORMS',
  GET_DYNAMIC_FIELDS_SCHEMAS: 'GET_DYNAMIC_FIELDS_SCHEMAS',
  ADD_TEMPLATE_VARIABLES_TO_DYNAMIC_FIELDS_SCHEMAS:
    'ADD_TEMPLATE_VARIABLES_TO_DYNAMIC_FIELDS_SCHEMAS',
  UPDATE_FORM: 'UPDATE_FORM',
  DELETE_FORM: 'DELETE_FORM',
  CREATE_FORM: 'CREATE_FORM',
  TOGGLE_TREE_ITEM: 'TOGGLE_TREE_ITEM',
  OPEN_FORM_PREVIEW: 'OPEN_FORM_PREVIEW',
  SET_PREVIEWING_FORM: 'SET_PREVIEWING_FORM_HTML',
  CREATE_FORM_BUNDLE_TESTING_TOOL: 'CREATE_FORM_BUNDLE_TESTING_TOOL',
});

export const toggleTreeItem = payload => ({
  type: FORMS_ACTION_TYPE.TOGGLE_TREE_ITEM,
  payload,
});

export const addTemplateVariablesToDynamicFieldsAction = payload => dispatch =>
  dispatch({
    type: FORMS_ACTION_TYPE.ADD_TEMPLATE_VARIABLES_TO_DYNAMIC_FIELDS_SCHEMAS,
    payload,
  });

export const getFormsAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await formsApi.getForms();

  const payloadData = data.map(form => getFormattedDataFormsNewApi(form));

  return dispatch({
    type: FORMS_ACTION_TYPE.GET_FORMS,
    payload: normalize(payloadData, [formsSchema]).entities.data,
  });
});

// Concurrent mounts (e.g. several Forms-typed condition rows) dedupe onto one request. The
// request is stamped with the formsInfoVersion it was issued under, so a caller arriving after
// an invalidation (form CRUD, identity swap) never joins a stale in-flight response — it starts
// a fresh request for the new state instead.
const pendingFormsInfo = { request: null, version: null };

const startFormsInfoRequest = version => {
  const request = formsApi.getFormsInfo().finally(() => {
    if (pendingFormsInfo.request === request) {
      pendingFormsInfo.request = null;
    }
  });

  pendingFormsInfo.request = request;
  pendingFormsInfo.version = version;

  return request;
};

export const getFormsInfoAction = catchAsyncActionError(() => async (dispatch, getState) => {
  if (selectIsFormsInfoLoaded(getState())) {
    return null;
  }

  const versionAtRequest = selectFormsInfoVersion(getState());
  const isPendingReusable =
    pendingFormsInfo.request !== null && pendingFormsInfo.version === versionAtRequest;

  const request = isPendingReusable
    ? pendingFormsInfo.request
    : startFormsInfoRequest(versionAtRequest);

  const { data } = await request;

  if (selectIsFormsInfoLoaded(getState())) {
    return null;
  }

  // Invalidated while this request was in flight — retry against the new state.
  if (selectFormsInfoVersion(getState()) !== versionAtRequest) {
    return dispatch(getFormsInfoAction());
  }

  return dispatch({
    type: FORMS_ACTION_TYPE.GET_FORMS_INFO,
    payload: data.map(getFormattedFormInfo),
  });
});

export const getLoanSpecificForms = catchAsyncActionError(params => async dispatch => {
  const { data } = await formsApi.getFormsByApplicationId(params);

  const payloadData = data.map(form => getFormattedDataFormsNewApi(form));

  return dispatch({
    type: FORMS_ACTION_TYPE.GET_LOAN_SPECIFIC_FORMS,
    payload: payloadData,
  });
});

export const getDynamicFieldsSchemaAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isFormsConsolidationEnabled = selectFeatureFlagByKey(
      FeatureFlag.FormsConsolidationEnabled,
    )(getState());

    const schemaApi = isFormsConsolidationEnabled ? formsApi : formApi;

    const { data } = await (params?.formId
      ? schemaApi.getFormSchemaById(params)
      : schemaApi.getFormSchema(params));

    return dispatch({
      type: FORMS_ACTION_TYPE.GET_DYNAMIC_FIELDS_SCHEMAS,
      payload: data,
    });
  },
);

export const createFormAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formsApi.createForm(params);

  const payloadData = getFormattedDataFormsNewApi(data);

  return dispatch({
    type: FORMS_ACTION_TYPE.CREATE_FORM,
    payload: payloadData,
  });
});

export const createFormPdfAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formsApi.createFormPdf(params);

  const payloadData = getFormattedDataFormsNewApi(data);

  return dispatch({
    type: FORMS_ACTION_TYPE.CREATE_FORM,
    payload: payloadData,
  });
});

export const updateFormAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formsApi.editForm(params);

  const payloadData = getFormattedDataFormsNewApi(data);

  return dispatch({
    type: FORMS_ACTION_TYPE.UPDATE_FORM,
    payload: payloadData,
  });
});

export const createFormOverrideAction = catchAsyncActionError(
  ({ formId, formData }) =>
    async dispatch => {
      const { data } = await formsApi.createFormOverride({ formId, formData });

      return dispatch({
        type: FORMS_ACTION_TYPE.UPDATE_FORM,
        payload: getFormattedDataFormsNewApi(data),
      });
    },
);

export const replaceFormTemplateContentAction = catchAsyncActionError(
  ({ formId, file = null }) =>
    async dispatch => {
      const { data } = file
        ? await formsApi.replaceFormTemplatePdf({ formId, file })
        : await formsApi.editFormTemplate({ formId, type: FormTemplateTypeNewApi.HTML_EMPTY });

      return dispatch({
        type: FORMS_ACTION_TYPE.UPDATE_FORM,
        payload: getFormattedDataFormsNewApi(data),
      });
    },
);

export const deleteFormAction = catchAsyncActionError(
  ({ message = 'Form removed', ...params }) =>
    async dispatch => {
      await formsApi.deleteForm(params);

      dispatch({
        type: FORMS_ACTION_TYPE.DELETE_FORM,
        payload: params,
      });

      dispatch(showMessageAction({ message, variant: 'success' }));
    },
);

export const createFormBundleTestingToolAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isFormsConsolidationEnabled = selectFeatureFlagByKey(
      FeatureFlag.FormsConsolidationEnabled,
    )(getState());

    const testingToolApi = isFormsConsolidationEnabled
      ? formsTestingToolApi
      : formBundleTestingToolApi;

    const { data } = await testingToolApi.createFormBundleTestingTool(params);

    return dispatch({
      type: FORMS_ACTION_TYPE.CREATE_FORM_BUNDLE_TESTING_TOOL,
      payload: data,
    });
  },
);
